import { z } from 'zod';
import { normalizeUsPhone, US_PHONE_INVALID_MESSAGE } from '../format/phone.js';
import { normalizeUsState, US_STATE_INVALID_MESSAGE } from '../geo/us-states.js';
import { normalizeUsZip, US_ZIP_INVALID_MESSAGE } from '../geo/zip.js';
import { PG_INT_MAX } from '../limits.js';
/**
 * A non-negative integer that may be left blank, no larger than `max`.
 *
 * Parses: `''` / whitespace / `null` → `''` (blank sentinel);
 * `undefined` → `undefined`; `'12'` / `12` → `12`; `'0'` → `0`.
 * Rejects: non-numeric text, negatives, non-integers, and (QA 2026-10-01
 * F-08) anything above `max` with "Enter N or less.", the same sentence the
 * server returns. `max` defaults to the int4 ceiling so no form can submit a
 * number the database cannot store.
 *
 * Feed the result through `blankToUndefined` or `blankToNull` before it
 * reaches a request body — never `Number()` it directly, which is the
 * bug this builder exists to prevent.
 */
export function optionalIntegerInput(max = PG_INT_MAX) {
    return z
        .preprocess((v) => {
        // `null` arrives when a form is seeded straight from an API payload
        // whose nullable column is empty; treat it as blank, not as 0
        // (`Number(null)` is 0, the same trap as `Number('')`).
        if (v === null)
            return '';
        return typeof v === 'string' ? v.trim() : v;
    }, z
        .union([z.literal(''), z.coerce.number().int().nonnegative()], {
        errorMap: () => ({ message: 'Enter a whole number, or leave blank' }),
    })
        .refine((v) => v === '' || v <= max, {
        message: `Enter ${max.toLocaleString('en-US')} or less.`,
    }))
        .optional();
}
/**
 * QA 2026-10-01 F-04 — a free-text input that is stored in one normalized
 * form. Blank stays `''` (the submit handler turns it into null or omits it);
 * a non-blank value is normalized, or rejected with `message`.
 */
function normalizedTextInput(normalize, message) {
    return z
        .string()
        .optional()
        .transform((v, ctx) => {
        if (v === undefined)
            return undefined;
        const trimmed = v.trim();
        if (trimmed === '')
            return '';
        const normalized = normalize(trimmed);
        if (normalized === null) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, message });
            return z.NEVER;
        }
        return normalized;
    });
}
/** US state: "al" or "Alabama" -> "AL"; blank allowed. */
export const usStateInput = normalizedTextInput(normalizeUsState, US_STATE_INVALID_MESSAGE);
/** US ZIP: "352031234" -> "35203-1234"; blank allowed. */
export const usZipInput = normalizedTextInput(normalizeUsZip, US_ZIP_INVALID_MESSAGE);
/** US phone: "205.555.0123" -> "(205) 555-0123"; blank allowed. */
export const usPhoneInput = normalizedTextInput(normalizeUsPhone, US_PHONE_INVALID_MESSAGE);
/**
 * Blank → `undefined`, so a spread omits the key entirely.
 *
 * Use for CREATE bodies. `POST /api/v1/accounts` declares
 * `employeeCount: { type: 'integer', minimum: 0, maximum: EMPLOYEE_COUNT_MAX }` with
 * `additionalProperties: false` — it does NOT accept null, so an unknown
 * value must be absent from the payload rather than explicitly nulled.
 */
export function blankToUndefined(value) {
    return value === '' || value === undefined ? undefined : value;
}
/**
 * Blank → `null`, an explicit instruction to clear the stored value.
 *
 * Use for PATCH bodies. `PATCH /api/v1/accounts/:id` declares
 * `employeeCount: { type: ['integer', 'null'], ... }`; omitting the key would
 * leave a previously stored number in place, so clearing the field on an
 * edit form requires sending null.
 */
export function blankToNull(value) {
    return value === '' || value === undefined ? null : value;
}
//# sourceMappingURL=form-inputs.js.map